import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity, CLASSIFIABLE_EXTENSIONS } from "@erp/shared-types";

/**
 * An inline category definition for classification.
 */
const inlineCategorySchema = z.object({
  name: z.string().describe("Category name"),
  description: z
    .string()
    .describe(
      "Description to help the AI understand when to apply this category",
    ),
});

/**
 * Classify Document Content Step
 *
 * Classifies a single document into user-configurable categories using
 * Extend AI's classify API. Lightweight version of Classify Email,
 * designed for use after fanout (e.g., per-sheet after splitting an Excel).
 *
 * Returns singular values (not arrays) — one classification result per document.
 */
export const classifyDocumentV2Step = defineStepConfig({
  type: "classify_document_v2",
  name: "Classify Document (V2)",
  description:
    "Classify a single document into configurable categories using Extend AI",
  category: "AI & Agents",

  inputSchema: z.object({
    document: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "The document to classify",
    ),
  }),

  outputSchema: z.object({
    document: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "Pass-through of the input document",
    ),
    categoryId: z.string().describe("Classification ID"),
    categoryName: z.string().describe("Human-readable category name"),
    confidence: z.number().describe("Classification confidence score (0-1)"),
    reasoning: z
      .string()
      .describe("Extend AI reasoning for the classification"),
  }),

  configSchema: z.object({
    classificationSource: z
      .enum(["enum", "inline"])
      .default("inline")
      .describe(
        'Where categories come from: "enum" for CoreEnum, "inline" for direct definition',
      ),
    coreEnumId: z
      .string()
      .uuid()
      .optional()
      .describe(
        "CoreEnum ID to load categories from (when classificationSource is enum)",
      ),
    categories: z
      .array(inlineCategorySchema)
      .optional()
      .describe(
        "Inline category definitions (when classificationSource is inline)",
      ),
    allowedFileExtensions: z
      .array(
        z.enum(CLASSIFIABLE_EXTENSIONS as unknown as [string, ...string[]]),
      )
      .min(1)
      .describe(
        'File extensions to classify (e.g. ["xlsx", "csv", "pdf"]). Documents with other extensions are skipped and classified as "other".',
      ),
    confidenceThreshold: z
      .number()
      .min(0)
      .max(1)
      .optional()
      .default(0.5)
      .describe(
        "Minimum confidence score — below this, result still returned but categoryName is set to 'other'",
      ),
    includeOther: z
      .boolean()
      .optional()
      .default(true)
      .describe('Whether to include an "other" fallback classification'),
    classificationRules: z
      .string()
      .optional()
      .describe("Natural language guidance for classification"),
    baseProcessor: z
      .enum(["classification_performance", "classification_light"])
      .optional()
      .describe("Extend AI classification model to use"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 2,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
    nonRetryableErrorTypes: [
      "WorkflowGuardrailBlockedError",
      "WorkflowGuardrailReviewRequiredError",
    ],
  },
});
